import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { 
  LogOut, MessageSquare, Send, Package, User, Clock, Check, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { playNotificationSound } from '../../utils/notificationSound';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminChat = () => {
  const { storeDomain, adminPath } = useParams();
  const { user, logout, getAuthHeader, getAdminBasePath, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const basePath = adminPath ? `/${adminPath}` : (storeDomain ? `/admin/${storeDomain}` : getAdminBasePath());
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const prevUnreadRef = useRef(0);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate(adminPath ? `/${adminPath}/login` : (storeDomain ? `/admin/${storeDomain}/login` : '/admin/login'));
      return;
    }
    if ((adminPath && user.admin_path && adminPath !== user.admin_path) || (storeDomain && user.store_domain && storeDomain !== user.store_domain)) {
      logout(); navigate(adminPath ? `/${adminPath}/login` : `/admin/${storeDomain}/login`, { replace: true }); return;
    }
    loadSessions();
    const interval = setInterval(loadSessions, 5000);
    return () => clearInterval(interval);
  }, [user, navigate, authLoading, storeDomain, adminPath]);

  useEffect(() => {
    if (selectedSession) {
      loadMessages(selectedSession);
      markAsRead(selectedSession);
      const interval = setInterval(() => loadMessages(selectedSession), 3000);
      return () => clearInterval(interval);
    }
  }, [selectedSession]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadSessions = async () => {
    try {
      const response = await axios.get(`${API}/chat/sessions`, {
        headers: getAuthHeader()
      });
      const newSessions = response.data;
      const totalUnread = newSessions.reduce((sum, s) => sum + (s.unread_count || 0), 0);
      if (totalUnread > prevUnreadRef.current && prevUnreadRef.current >= 0) {
        playNotificationSound('message');
      }
      prevUnreadRef.current = totalUnread;
      setSessions(newSessions);
    } catch (error) {
      console.error('Error loading sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/chat/messages/${sessionId}`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const markAsRead = async (sessionId) => {
    try {
      await axios.put(`${API}/chat/messages/mark-read/${sessionId}`, {}, {
        headers: getAuthHeader()
      });
      loadSessions();
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || !selectedSession) return;

    try {
      await axios.post(`${API}/chat/messages`, {
        session_id: selectedSession,
        sender: 'admin',
        message: inputMessage,
        customer_name: 'SuperMercado'
      }, { headers: getAuthHeader() });

      setInputMessage('');
      loadMessages(selectedSession);
    } catch (error) {
      toast.error('Error al enviar mensaje');
      console.error(error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate(adminPath ? `/${adminPath}/login` : (storeDomain ? `/admin/${storeDomain}/login` : '/admin/login'));
  };

  const formatTime = (timestamp) => {
    return new Date(typeof timestamp === 'string' && !timestamp.endsWith('Z') ? timestamp + 'Z' : timestamp).toLocaleString('es-EC', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Guayaquil'
    });
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <MessageSquare size={48} className="animate-spin text-accent-text" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-section)' }}>
      {/* Header */}
      <div className="bg-white border-b" style={{ borderColor: 'var(--border-light)' }}>
        <div className="container py-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <MessageSquare className="text-accent-text flex-shrink-0" size={24} />
              <div className="min-w-0">
                <h1 className="text-base md:text-lg font-bold truncate">Chat con Clientes</h1>
                <p className="text-xs text-text-muted">Conversaciones</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => navigate(`${basePath}/dashboard`)} className="btn-secondary text-sm">
                <Package size={16} className="mr-1" />
                <span className="hidden sm:inline">Productos</span>
              </button>
              <button onClick={handleLogout} className="btn-secondary text-sm">
                <LogOut size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container py-3 md:py-6">
        <div 
          className="bg-white rounded-xl border overflow-hidden flex flex-col md:flex-row"
          style={{ 
            borderColor: 'var(--border-light)',
            height: 'calc(100vh - 140px)',
            minHeight: '400px'
          }}
        >
          {/* Sessions List */}
          <div 
            className={`${selectedSession ? 'hidden md:flex' : 'flex'} w-full md:w-80 border-r flex-col flex-shrink-0`}
            style={{ borderColor: 'var(--border-light)' }}
          >
            <div className="p-4 border-b" style={{ borderColor: 'var(--border-light)' }}>
              <h3 className="font-semibold text-text-primary">Conversaciones</h3>
              <p className="text-xs text-text-muted mt-1">{sessions.length} chats activos</p>
            </div>

            <div className="flex-1 overflow-y-auto">
              {sessions.length === 0 ? (
                <div className="p-6 text-center text-text-muted">
                  <MessageSquare size={40} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No hay conversaciones</p>
                </div>
              ) : (
                sessions.map((session) => (
                  <div
                    key={session.session_id}
                    onClick={() => setSelectedSession(session.session_id)}
                    className={`p-4 border-b cursor-pointer transition-colors ${
                      selectedSession === session.session_id 
                        ? 'bg-accent-wash' 
                        : 'hover:bg-section'
                    }`}
                    style={{ borderColor: 'var(--border-light)' }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-10 h-10 rounded-full flex items-center justify-center"
                          style={{ background: 'var(--accent-primary)' }}
                        >
                          <User size={20} className="text-white" />
                        </div>
                        <div>
                          <p className="font-medium text-text-primary">
                            {session.customer_name || 'Cliente'}
                          </p>
                          <p className="text-xs text-text-muted truncate max-w-[150px]">
                            {session.last_message}
                          </p>
                        </div>
                      </div>
                      {session.unread_count > 0 && (
                        <span 
                          className="px-2 py-1 rounded-full text-xs font-bold text-white"
                          style={{ background: 'var(--accent-primary)' }}
                        >
                          {session.unread_count}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 mt-2 text-xs text-text-muted">
                      <Clock size={12} />
                      {formatTime(session.last_message_time)}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Chat Area */}
          <div className={`flex-1 flex flex-col ${!selectedSession ? 'hidden md:flex' : 'flex'}`}>
            {selectedSession ? (
              <>
                {/* Chat Header */}
                <div className="p-3 md:p-4 border-b" style={{ borderColor: 'var(--border-light)' }}>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setSelectedSession(null)} className="p-1.5 hover:bg-gray-100 rounded-lg md:hidden flex-shrink-0">
                      <ArrowLeft size={18} />
                    </button>
                    <div 
                      className="w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: 'var(--accent-primary)' }}
                    >
                      <User size={16} className="text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-text-primary">
                        {sessions.find(s => s.session_id === selectedSession)?.customer_name || 'Cliente'}
                      </p>
                      <p className="text-xs text-accent-text">Conversación activa</p>
                    </div>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.sender === 'admin' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className="max-w-[70%] px-4 py-2 rounded-lg"
                        style={{
                          background: msg.sender === 'admin' 
                            ? 'var(--accent-primary)' 
                            : 'var(--bg-section)',
                          color: msg.sender === 'admin' ? 'white' : 'var(--text-body)'
                        }}
                      >
                        <p className="text-sm">{msg.message}</p>
                        <div className="flex items-center justify-end gap-1 mt-1">
                          <p className="text-xs opacity-70">
                            {new Date(typeof msg.timestamp === 'string' && !msg.timestamp.endsWith('Z') ? msg.timestamp + 'Z' : msg.timestamp).toLocaleTimeString('es-EC', { 
                              hour: '2-digit', 
                              minute: '2-digit',
                              timeZone: 'America/Guayaquil'
                            })}
                          </p>
                          {msg.sender === 'admin' && (
                            <Check size={12} className="opacity-70" />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <form 
                  onSubmit={handleSendMessage} 
                  className="p-4 border-t"
                  style={{ borderColor: 'var(--border-light)' }}
                >
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      placeholder="Escribe tu respuesta..."
                      className="flex-1 px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                      style={{
                        borderColor: 'var(--border-light)',
                        '--tw-ring-color': 'var(--accent-primary)'
                      }}
                    />
                    <button type="submit" className="btn-primary px-6">
                      <Send size={20} />
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center text-text-muted">
                  <MessageSquare size={64} className="mx-auto mb-4 opacity-50" />
                  <p className="text-lg">Selecciona una conversación</p>
                  <p className="text-sm mt-1">para ver los mensajes</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminChat;
